import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import { size as sizeTokens, radius, fontFamily, transition } from "../tokens";

/**
 * Square icon-only button. Requires an `ariaLabel` for accessibility.
 *
 * @param {React.ReactNode}   children    - The icon element (e.g. <X size={14} />)
 * @param {Function}          onClick
 * @param {boolean}           disabled
 * @param {'ghost'|'light'|'primary'|'destructive'|'outlined'} variant - default "ghost"
 * @param {'extraSmall'|'small'|'medium'|'large'}              size    - default "medium"
 * @param {string}            ariaLabel   - Required: describes the action for screen readers
 * @param {string}            className
 */
const IconButton = ({
  children,
  onClick,
  disabled = false,
  variant = "ghost",
  size = "medium",
  ariaLabel,
  className,
  ...props
}) => {
  const classes = useStyles();

  return (
    <button
      className={clsx(classes.root, classes[variant], classes[size], className)}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    cursor: "pointer",
    borderRadius: radius.sm,
    fontFamily: fontFamily.base,
    flexShrink: 0,
    transition: `background ${transition.slow}, color ${transition.slow}, opacity ${transition.slow}`,
    padding: 0,
    "&:disabled": {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },

  // ── Variants ────────────────────────────────────────────────────────────────
  ghost: {
    backgroundColor: "transparent",
    color: "#0C0E29",
    "&:hover:not(:disabled)": {
      backgroundColor: "#EDEDED",
    },
  },
  light: {
    backgroundColor: "#F8F8F8",
    color: "#0C0E29",
    "&:hover:not(:disabled)": {
      backgroundColor: "#EDEDED",
    },
  },
  primary: {
    backgroundColor: "var(--color-primary)",
    color: "#fff",
    "&:hover:not(:disabled)": {
      backgroundColor: "var(--color-primary-light)",
    },
  },
  destructive: {
    backgroundColor: "#D84949",
    color: "#F8F8F8",
    "&:hover:not(:disabled)": {
      backgroundColor: "#C54242",
    },
  },
  outlined: {
    backgroundColor: "transparent",
    border: "1px solid var(--color-primary)",
    color: "var(--color-primary)",
    "&:hover:not(:disabled)": {
      backgroundColor: "var(--color-primary-light)",
      borderColor: "var(--color-primary-light)",
      color: "#fff",
    },
  },

  // ── Sizes — square, derived from the shared size tokens ───────────────────
  extraSmall: {
    width:  sizeTokens.extraSmall.height,
    height: sizeTokens.extraSmall.height,
    "& svg": { width: sizeTokens.extraSmall.iconSize, height: sizeTokens.extraSmall.iconSize },
  },
  small: {
    width:  sizeTokens.small.height,
    height: sizeTokens.small.height,
    "& svg": { width: sizeTokens.small.iconSize, height: sizeTokens.small.iconSize },
  },
  medium: {
    width:  sizeTokens.medium.height,
    height: sizeTokens.medium.height,
    "& svg": { width: sizeTokens.medium.iconSize, height: sizeTokens.medium.iconSize },
  },
  large: {
    width:  sizeTokens.large.height,
    height: sizeTokens.large.height,
    "& svg": { width: sizeTokens.large.iconSize, height: sizeTokens.large.iconSize },
  },
}));

export default IconButton;
